import { useId } from 'react';
import { NAMESPACES, factSpec, type Namespace } from '../../lib/story/facts';
import { COND_OPS, type CondDef } from '../../lib/story/types';
import type { Issue } from '../../lib/validate';
import { IssueList, inputCls, smallBtn } from '../forms/fields';

export type CondHints = {
  /** var.* suffixes to offer (hidden vars and tells, or scenario vars). */
  vars?: string[];
  /** cast roles to offer for cast.<role>.*. */
  roles?: string[];
  /** Raid facts are only meaningful in table cases and option requires. */
  allowRaid?: boolean;
  /** The namespaces to offer (default: the engine's 11; scenario triggers pass `SCENARIO_NAMESPACES`). */
  namespaces?: Namespace[];
};

const leafOf = (fact: string): CondDef => ({
  fact,
  op: 'eq',
  value: factSpec(fact)?.type === 'bool' ? 'true' : '',
  args: Array.from({ length: factSpec(fact)?.minArgs ?? 0 }, () => ''),
  values: [],
  all: [],
  any: [],
  not: null,
});

type Mode = 'leaf' | 'all' | 'any' | 'not';
const modeOf = (c: CondDef): Mode =>
  c.all?.length ? 'all' : c.any?.length ? 'any' : c.not ? 'not' : 'leaf';

/** Issues for this node itself (its own path and its leaf fields); children show their own. */
const at = (issues: Record<string, Issue[]> | undefined, path: string) =>
  issues
    ? ['', 'fact', 'op', 'value', 'values', 'args'].flatMap(
        (k) => issues[k ? (path ? `${path}.${k}` : k) : path] ?? [],
      )
    : [];

/**
 * The condition picker: a typed tree (shared-cs `CondDef`), built by namespace instead of free text. A leaf picks a namespace, then a
 * fact (or types the suffix of a dynamic one like `var.companion.bitten`), its arguments, an op and a value; `all`, `any` and `not`
 * combine. `issues` are keyed by path relative to this condition (`all.1.op`).
 */
export function ConditionPicker({
  label,
  value,
  onChange,
  hints = {},
  issues,
  optional = true,
}: {
  label: string;
  value: CondDef | null | undefined;
  onChange: (next: CondDef | null) => void;
  hints?: CondHints;
  issues?: Record<string, Issue[]>;
  optional?: boolean;
}) {
  return (
    <div className="sm:col-span-2">
      <span className="block text-xs font-semibold uppercase tracking-wide">{label}</span>
      {value == null ? (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-bruise">Always (no condition).</span>
          <button type="button" className={smallBtn} onClick={() => onChange(leafOf('var.'))}>
            Add condition
          </button>
        </div>
      ) : (
        <CondNode
          label={label}
          value={value}
          onChange={(v) => onChange(v)}
          onRemove={optional ? () => onChange(null) : undefined}
          hints={hints}
          issues={issues}
          path=""
        />
      )}
    </div>
  );
}

function CondNode({
  label,
  value,
  onChange,
  onRemove,
  hints,
  issues,
  path,
}: {
  label: string;
  value: CondDef;
  onChange: (next: CondDef) => void;
  onRemove?: () => void;
  hints: CondHints;
  issues?: Record<string, Issue[]>;
  path: string;
}) {
  const mode = modeOf(value);
  const sub = (p: string) => (path ? `${path}.${p}` : p);
  const setMode = (m: Mode) => {
    if (m === mode) return;
    if (m === 'leaf') onChange(leafOf('var.'));
    else if (m === 'not')
      onChange({ all: [], any: [], not: value, fact: '', args: [], values: [] });
    else
      onChange({
        all: m === 'all' ? [value] : [],
        any: m === 'any' ? [value] : [],
        not: null,
        fact: '',
        args: [],
        values: [],
      });
  };
  return (
    <div className="mt-1 rounded border border-newsprint bg-white p-1.5" data-testid="cond-node">
      <div className="flex flex-wrap items-center gap-1">
        <select
          aria-label={`${label} kind`}
          className={`${inputCls} w-auto`}
          value={mode}
          onChange={(e) => setMode(e.target.value as Mode)}
        >
          <option value="leaf">fact</option>
          <option value="all">all of</option>
          <option value="any">any of</option>
          <option value="not">not</option>
        </select>
        {mode === 'leaf' && (
          <LeafEditor label={label} value={value} onChange={onChange} hints={hints} />
        )}
        {onRemove && (
          <button
            type="button"
            className={`${smallBtn} text-blam`}
            aria-label={`Remove ${label}`}
            onClick={onRemove}
          >
            ✕
          </button>
        )}
      </div>
      {(mode === 'all' || mode === 'any') && (
        <div className="ml-4 border-l-2 border-newsprint pl-2">
          {(value[mode] ?? []).map((child, i) => (
            <CondNode
              key={i}
              label={`${label} ${mode} ${i + 1}`}
              value={child}
              hints={hints}
              issues={issues}
              path={sub(`${mode}.${i}`)}
              onChange={(c) =>
                onChange({ ...value, [mode]: (value[mode] ?? []).map((x, j) => (j === i ? c : x)) })
              }
              onRemove={() => {
                const next = (value[mode] ?? []).filter((_, j) => j !== i);
                onChange(next.length ? { ...value, [mode]: next } : leafOf('var.'));
              }}
            />
          ))}
          <button
            type="button"
            className={`${smallBtn} mt-1`}
            onClick={() => onChange({ ...value, [mode]: [...(value[mode] ?? []), leafOf('var.')] })}
          >
            Add to {mode}
          </button>
        </div>
      )}
      {mode === 'not' && value.not && (
        <div className="ml-4 border-l-2 border-newsprint pl-2">
          <CondNode
            label={`${label} not`}
            value={value.not}
            hints={hints}
            issues={issues}
            path={sub('not')}
            onChange={(c) => onChange({ ...value, not: c })}
          />
        </div>
      )}
      <IssueList issues={at(issues, path)} />
    </div>
  );
}

function LeafEditor({
  label,
  value,
  onChange,
  hints,
}: {
  label: string;
  value: CondDef;
  onChange: (next: CondDef) => void;
  hints: CondHints;
}) {
  const listId = useId();
  const fact = value.fact ?? '';
  const all = hints.namespaces ?? NAMESPACES;
  const ns =
    all.find((n) => n.facts.some((x) => x.name === fact)) ??
    all.find((n) => n.dynamic && fact.startsWith(n.dynamic.prefix)) ??
    all.find(
      (n) => n.id === (fact.split('.')[0] === 'encounter' ? 'building' : fact.split('.')[0]),
    ) ??
    all[0]!;
  const spec = factSpec(fact);
  const namespaces = all.filter(
    (n) => hints.allowRaid !== false || n.id !== 'raid' || hints.namespaces,
  );
  const suggestions =
    ns.id === 'var'
      ? (hints.vars ?? [])
      : ns.id === 'cast'
        ? (hints.roles ?? []).flatMap((r) => [`${r}.role`, `${r}.condition`, `${r}.hpPct`])
        : [];
  const set = (patch: Partial<CondDef>) => onChange({ ...value, ...patch });
  return (
    <>
      <select
        aria-label={`${label} namespace`}
        className={`${inputCls} w-auto`}
        value={ns.id}
        onChange={(e) => {
          const n = all.find((x) => x.id === e.target.value)!;
          onChange(leafOf(n.dynamic ? n.dynamic.prefix : (n.facts[0]?.name ?? '')));
        }}
      >
        {namespaces.map((n) => (
          <option key={n.id} value={n.id}>
            {n.label}
          </option>
        ))}
      </select>
      {ns.dynamic ? (
        <>
          <input
            aria-label={`${label} fact`}
            className={`${inputCls} w-48 font-mono`}
            list={listId}
            placeholder={ns.dynamic.hint}
            value={fact.slice(ns.dynamic.prefix.length)}
            onChange={(e) => set({ fact: ns.dynamic!.prefix + e.target.value })}
          />
          <datalist id={listId}>
            {suggestions.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </>
      ) : (
        <select
          aria-label={`${label} fact`}
          className={`${inputCls} w-auto font-mono`}
          value={fact}
          onChange={(e) => onChange(leafOf(e.target.value))}
        >
          {ns.facts.map((f) => (
            <option key={f.name} value={f.name}>
              {f.name}
              {f.args ? `(${f.args.join(', ')})` : ''}
            </option>
          ))}
        </select>
      )}
      {(spec?.args ?? []).map((a, i) => (
        <input
          key={a}
          aria-label={`${label} ${a}`}
          className={`${inputCls} w-28 font-mono`}
          placeholder={a}
          value={value.args?.[i] ?? ''}
          onChange={(e) => {
            const args = [...(value.args ?? [])];
            while (args.length <= i) args.push('');
            args[i] = e.target.value;
            // Optional trailing arguments (scope:account) are left out when blank: the server counts them.
            while (args.length > (spec?.minArgs ?? 0) && args[args.length - 1] === '') args.pop();
            set({ args });
          }}
        />
      ))}
      <select
        aria-label={`${label} op`}
        className={`${inputCls} w-auto`}
        value={value.op ?? 'eq'}
        onChange={(e) => set({ op: e.target.value })}
      >
        {COND_OPS.filter((o) => spec?.type === 'int' || !spec || (o !== 'gte' && o !== 'lte')).map(
          (o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ),
        )}
      </select>
      {value.op === 'in' ? (
        <input
          aria-label={`${label} values`}
          className={`${inputCls} w-40 font-mono`}
          placeholder="a, b, c"
          value={(value.values ?? []).join(', ')}
          onChange={(e) =>
            set({
              values: e.target.value
                .split(',')
                .map((x) => x.trim())
                .filter(Boolean),
            })
          }
        />
      ) : spec?.type === 'bool' ||
        (ns.id === 'var' && (value.value === 'true' || value.value === 'false')) ? (
        <select
          aria-label={`${label} value`}
          className={`${inputCls} w-auto`}
          value={value.value ?? 'true'}
          onChange={(e) => set({ value: e.target.value })}
        >
          <option value="true">true</option>
          <option value="false">false</option>
          {ns.id === 'var' && <option value="">(type a value)</option>}
        </select>
      ) : (
        <input
          aria-label={`${label} value`}
          className={`${inputCls} w-28 font-mono`}
          inputMode={spec?.type === 'int' ? 'numeric' : undefined}
          value={value.value ?? ''}
          onChange={(e) => set({ value: e.target.value })}
        />
      )}
    </>
  );
}
